import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchResources,
  fetchModules,
  fetchSkeletonsByRoot,
} from '../services/generatorBatchApi';

/**
 * Carica le risorse di sola lettura (prodotti, radici scheletri, moduli) tramite
 * gli endpoint legacy autorizzati dal contratto. Espone caricamenti on-demand
 * per moduli del prodotto e scheletri di una root, con caching in memoria e
 * annullamento delle richieste obsolete.
 */
export function useBatchResources() {
  const [products, setProducts] = useState([]);
  const [skeletonRoots, setSkeletonRoots] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [modulesByProduct, setModulesByProduct] = useState({});
  const [skeletonsByRoot, setSkeletonsByRoot] = useState({});
  const [loading, setLoading] = useState(true);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [error, setError] = useState(null);

  const rootAborts = useRef(new Map());
  const moduleAbort = useRef(null);

  // Caricamento iniziale.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchResources(controller.signal)
      .then((data) => {
        setProducts(Array.isArray(data?.products) ? data.products : []);
        setSkeletonRoots(Array.isArray(data?.skeletonRoots) ? data.skeletonRoots : []);
        setAllModules(Array.isArray(data?.modules) ? data.modules : []);
        setError(null);
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setError('Impossibile caricare le risorse iniziali.');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  /** Moduli associati a un prodotto (con cache). */
  const loadModulesForProduct = useCallback(
    (productId) => {
      if (productId == null || productId === '') return;
      if (modulesByProduct[productId]) return; // già in cache
      if (moduleAbort.current) moduleAbort.current.abort();
      const controller = new AbortController();
      moduleAbort.current = controller;
      setModulesLoading(true);
      fetchModules(productId, controller.signal)
        .then((mods) => {
          setModulesByProduct((prev) => ({
            ...prev,
            [productId]: Array.isArray(mods) ? mods : [],
          }));
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          setModulesByProduct((prev) => ({ ...prev, [productId]: [] }));
        })
        .finally(() => setModulesLoading(false));
    },
    [modulesByProduct],
  );

  /** Scheletri (.skl selezionabili) di una root (con cache). */
  const loadSkeletonsForRoot = useCallback(
    (root) => {
      if (!root) return;
      if (skeletonsByRoot[root]) return;
      const existing = rootAborts.current.get(root);
      if (existing) existing.abort();
      const controller = new AbortController();
      rootAborts.current.set(root, controller);
      fetchSkeletonsByRoot(root, controller.signal)
        .then((list) => {
          setSkeletonsByRoot((prev) => ({
            ...prev,
            [root]: Array.isArray(list) ? list : [],
          }));
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          setSkeletonsByRoot((prev) => ({ ...prev, [root]: [] }));
        });
    },
    [skeletonsByRoot],
  );

  // Cleanup di eventuali richieste pendenti allo smontaggio.
  useEffect(() => {
    const roots = rootAborts.current;
    return () => {
      if (moduleAbort.current) moduleAbort.current.abort();
      roots.forEach((c) => c.abort());
      roots.clear();
    };
  }, []);

  return {
    products,
    skeletonRoots,
    allModules,
    modulesByProduct,
    skeletonsByRoot,
    loading,
    modulesLoading,
    error,
    loadModulesForProduct,
    loadSkeletonsForRoot,
  };
}
